import 'react-hook-consent/dist/styles/style.css'
import '@solana/wallet-adapter-react-ui/styles.css'
import '../styles/globals.css'

import { soehne } from '../fonts'

import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react'

import type { AppProps } from 'next/app'
import {
  WalletError,
  WalletDisconnectedError,
  WalletNotReadyError,
  WalletConnectionError,
} from '@solana/wallet-adapter-base'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import { ReactNode, useCallback, useMemo } from 'react'
import {
  SolflareWalletAdapter,
  PhantomWalletAdapter,
} from '@solana/wallet-adapter-wallets'
import {
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
  SolanaMobileWalletAdapter,
} from '@solana-mobile/wallet-adapter-mobile'

import { MainContainer } from '../components/MainContainer'
import { CheckoutCartProvider } from '../providers/CartModalProvider'
import { APP_URL, RPC_ENDPOINT, RPC_NETWORK } from '../utils/constants'
import { VerifyWalletProvider } from '../providers/VerifyWalletProvider'
import { InfoBoxProvider } from '../providers/InfoBoxProvider'
import { useInfoBox } from '../context/useInfoBox'
import { GaProvider, useGaHandler } from '../providers/GaProvider'
import { ConsentProvider } from 'react-hook-consent'
import { consentOptions } from '../utils/consentOptions'
import { GDPRConsentBanner } from '../components/GDPR'
import { TwitterConversionProvider } from '../providers/TwitterConversionContext'
import SitewideTopAlert from '../components/SitewideTopAlert'
import { LightboxModalProvider } from '../providers/LightboxModalProvider'

const CONNECTION_CONFIG = { commitment: 'processed' }
const ENDPOINT = /*#__PURE__*/ RPC_ENDPOINT

const EXCLUDED_PAGES = ['/post-order']

function App({ children }: { children: ReactNode }) {
  const { enqueueInfo } = useInfoBox()

  const handleWalletError = useCallback(
    async (e: WalletError) => {
      if (
        e instanceof WalletDisconnectedError ||
        e instanceof WalletNotReadyError ||
        e instanceof WalletConnectionError
      )
        return
      enqueueInfo(`${e.name}: ${e.message}`, { variant: 'error' })
      const { captureException } = await import('@sentry/nextjs')
      captureException(e)
    },
    [enqueueInfo]
  )
  const adapters = useMemo(
    () =>
      typeof window === 'undefined'
        ? [] // No wallet adapters when server-side rendering.
        : [
            new SolanaMobileWalletAdapter({
              addressSelector: createDefaultAddressSelector(),
              appIdentity: {
                icon: '/favicon-32x32.png',
                name: 'Solana Mobile Storefront',
                uri: APP_URL,
              },
              authorizationResultCache: createDefaultAuthorizationResultCache(),
              cluster: RPC_NETWORK,
              onWalletNotFound: createDefaultWalletNotFoundHandler(),
            }),
            new SolflareWalletAdapter(),
            new PhantomWalletAdapter(),
          ],
    []
  )
  return (
    // @ts-ignore
    <ConnectionProvider config={CONNECTION_CONFIG} endpoint={ENDPOINT}>
      <WalletProvider
        onError={handleWalletError}
        wallets={adapters}
        autoConnect={false}
      >
        <WalletModalProvider>
          <VerifyWalletProvider>
            <CheckoutCartProvider>
              <SitewideTopAlert
                message="Get everything Saga has to offer at a lower price. Now $599"
                excluded={EXCLUDED_PAGES}
              />
              <MainContainer>{children}</MainContainer>
            </CheckoutCartProvider>
          </VerifyWalletProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  )
}

function SagaStorefrontApp({ Component, pageProps }: AppProps) {
  const ga = useGaHandler()

  return (
    <ConsentProvider options={consentOptions}>
      <main className={`${soehne.variable} font-sans`}>
        <TwitterConversionProvider>
          <GaProvider value={ga}>
            <InfoBoxProvider>
              <LightboxModalProvider>
                <App>
                  <Component {...pageProps} />
                  <GDPRConsentBanner />
                </App>
              </LightboxModalProvider>
            </InfoBoxProvider>
          </GaProvider>
        </TwitterConversionProvider>
      </main>
    </ConsentProvider>
  )
}

export default SagaStorefrontApp
